import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useTours } from '@/hooks/useTours'
import { getTourForPath, shouldAutoStartTour } from '@/services/tours/tourCatalog'

/**
 * Abre sola, y una única vez, la guía del panel. Los demás módulos tienen
 * guía, pero solo se lanzan desde el botón "Guía" del header: nada de
 * popovers apareciendo por sorpresa al navegar.
 */
export function TourAutoStart() {
  const tenantId = useAuthStore((s) => s.tenantId)
  const location = useLocation()
  const { startTour, hasSeenTour, autoTourSuppressed } = useTours()

  const tour = getTourForPath(location.pathname)
  const tourId = tour?.id
  const tourPath = tour?.path
  const firstStepSelector = tour?.steps[0]?.element

  useEffect(() => {
    if (!tenantId || !tourId || !tourPath || !firstStepSelector) return
    if (location.pathname !== tourPath) return
    if (!shouldAutoStartTour({ tour, hasSeen: hasSeenTour(tourId), autoSuppressed: autoTourSuppressed() })) {
      return
    }
    if (typeof document === 'undefined') return

    let cancelled = false
    let observer: MutationObserver | null = null
    let frameId: number | null = null

    const tryLaunch = (): boolean => {
      if (cancelled) return true
      const anchor = document.querySelector(firstStepSelector)
      if (!anchor) return false
      frameId = window.requestAnimationFrame(() => {
        if (!cancelled) {
          startTour(tourId)
        }
      })
      return true
    }

    if (!tryLaunch() && document.body) {
      observer = new MutationObserver(() => {
        if (tryLaunch() && observer) {
          observer.disconnect()
          observer = null
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      cancelled = true
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [
    tenantId,
    location.pathname,
    tour,
    tourId,
    tourPath,
    firstStepSelector,
    hasSeenTour,
    autoTourSuppressed,
    startTour,
  ])

  return null
}
